/** Any profile page: `/profile/<something>`. */
const PROFILE_ROUTE = /^\/profile\/[^/]/

/** `/profile/me`, as a whole segment — not a `me`-prefixed id or `/members`. */
const ME_SEGMENT = /^\/profile\/me(?=\/|$)/

/**
 * Every profile page requires a signed-in visitor, and `/profile/me` resolves
 * to that visitor's real id before anything renders.
 *
 * ── Why the whole section, and not just `me` ─────────────────────────────
 *
 * A profile is the claim surface. The bot hands out buttons reading "Claim
 * your rewards" that land on `/profile/<id>`, and signed out there was
 * nothing on that page to press.
 *
 * Not because the claim controls were broken — because the page correctly
 * decided you were a stranger. `Profile.vue` takes `userID` from the route
 * param and `ownID` from the store, so with nobody signed in `isOwnProfile`
 * is false and `isViewingOther` is true: you got the read-only view of
 * yourself. No reward track, no Customize, no claim buttons, and nothing
 * saying why — one click after being told to go and claim something.
 *
 * (The other shape of this, a reward track rendered with every card
 * unclaimable, needs `userID` itself to be null, which only ever happened on
 * a signed-out `/profile/me`. That one has been redirected here since this
 * guard was first written.)
 *
 * Gating the whole section is what makes the page's answer match the button
 * that sent you: signed in is now the only way to be here, so the only two
 * states left are your profile and somebody else's.
 *
 * It also makes the sign-in the first thing a new player does with a reward
 * they have already earned, which is a better introduction to the site than
 * a page of things they cannot touch.
 *
 * `me` still resolves to the real id afterwards, because `me` is not
 * shareable: paste it to someone and they see their own profile. Every other
 * profile URL is left exactly as it is — a link to a named person means the
 * same thing to everybody who clicks it, which is the entire reason the bot
 * builds them from an id.
 *
 * ── This is a courtesy, not enforcement ──────────────────────────────────
 *
 * Same footing as `ownProfileGuard`. `GET /api/profile/:id` is unauthenticated
 * and stays that way, so nothing here makes a profile private — it decides
 * what the site *offers*, not what the API will answer.
 *
 * ── Signed in is decided by the token ────────────────────────────────────
 *
 * Never by the cached id. `getUserId` falls back to localStorage, which
 * outlives the Discord token, so a lapsed session would otherwise resolve and
 * show someone their own profile as a visitor sees it with no way to tell the
 * difference. The cache still answers *which* signed-in user, but only once
 * the token has said there is one.
 *
 * The token itself is readable synchronously — `getToken` falls back to
 * `document.cookie` — so a hard load of `/profile/<id>` by a signed-in visitor
 * does not bounce through /login while a store hydrates.
 *
 * ── Why this is async ────────────────────────────────────────────────────
 *
 * One case is neither state yet: the moment after a first-ever login, when
 * the token cookie is set but nothing has fetched the identity behind it.
 * Treating that as signed out would bounce a freshly signed-in user back to
 * /login, which is a loop. So the guard resolves the identity and waits.
 *
 * `loadUser` is `fetchUserInfo`, which asks Discord and — this is the part
 * that makes it usable as the arbiter — clears the cached user and commits
 * null if the token is rejected. An expired token therefore resolves to
 * "signed out" and lands on /login, rather than hanging or half-resolving.
 *
 * The wait only happens on that cold path. With a cached id, the id is
 * knowable synchronously and the first committed route is already the real
 * one: no second render, no flash.
 *
 * @param {object} session
 * @param {() => string|null} session.isLoggedIn Truthy while a token is held.
 * @param {() => string|null} session.getUserId The signed-in user's id, if known.
 * @param {() => Promise<any>} session.loadUser Resolves the identity behind the token.
 * @returns {(to: object, from: object, next: Function) => void} A vue-router guard.
 */
export function createProfileGuard({ isLoggedIn, getUserId, loadUser }) {
    return async function profileGuard(to, from, next) {
        // Matched on the path rather than on `to.params.id`, which is only
        // populated for routes that declare an `:id` — a second such route
        // added anywhere else would silently start being gated by this.
        if (!PROFILE_ROUTE.test(to.path)) return next()

        /**
         * Where to come back to. The hash is dropped deliberately: safeNext
         * rejects a fragment, and a rejected `next` is not an error — it
         * silently falls back to /shop. Losing an anchor is better than
         * losing the return trip.
         *
         * The query survives, which matters here: every profile link the bot
         * hands out carries `?ref=`, and safeNext is written to keep it.
         */
        const back = to.fullPath.split('#')[0]
        const toLogin = () => next({ path: '/login', query: { next: back }, replace: true })

        if (!isLoggedIn()) return toLogin()

        // Resolved for *every* profile, not only `/profile/me`.
        //
        // `me` needs it to rewrite the URL, but a named profile needs it just
        // as much and used to skip it: `Profile.vue` decides whose page this
        // is by comparing the route param against `getUser.id`, and that is
        // `state.user.id` — null on every fresh page load, with no
        // localStorage fallback of its own. Signed in but not yet identified,
        // the owner therefore failed their own `isOwnProfile` check and got
        // the read-only stranger's view of themselves until the fetch landed,
        // then a re-render into the real thing.
        //
        // Which is precisely the path this guard exists to serve:
        // `Authenticate.vue` finishes OAuth with `window.location = next`, a
        // full page load with a cold store, so a first-time player following
        // "Claim your rewards" arrived exactly that way.
        //
        // Only the cold path waits. With an id already cached this is
        // synchronous, same as before.
        let ownID = getUserId()
        // Signed in, but we don't yet know as whom.
        if (!ownID) {
            await loadUser().catch(() => {})
            ownID = getUserId()
        }
        // The token was there but Discord would not honour it.
        if (!ownID) return toLogin()

        // The page names somebody, and nothing needs rewriting — that
        // includes your own id spelled out, which is already canonical.
        if (to.params.id !== 'me') return next()

        // Rewritten as a path rather than a named location.
        //
        // A named location was the first cut and is arguably tidier, but
        // vue-router builds the URL for a child with `path: ''` as parent +
        // '/', so `/profile/me` became `/profile/<id>/` — a trailing slash on
        // the one URL this whole guard exists to make worth copying.
        //
        // The pattern is anchored and matches a whole segment, so
        // `/profile/members` is untouched and only the leading `me` is
        // replaced; any child path after it comes along by itself.
        //
        // `query` and `hash` still cross as structured data, which is what
        // keeps `?ref=` and Stripe's `?subscribed=1` intact without this
        // having to parse or rebuild a query string.
        next({
            path: to.path.replace(ME_SEGMENT, `/profile/${ownID}`),
            query: to.query,
            hash: to.hash,
            // Replace, so Back leaves the profile rather than returning to
            // `/profile/me` and being bounced straight back here.
            replace: true,
        })
    }
}
